import {
    MATCH_TYPE_EXACT_IGNORE_CASE,
    MATCH_TYPE_WILDCARD,
    SimpleFieldFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import { isString } from "lodash-es";
import moment from "moment-timezone";

import { MatchRule, OrFilter } from "org/pageState/models";

// NOTE: this definition is slightly different in Legacy, because legacy has some variants of matchType which are not used in V2.
type ExactMatchType = typeof MATCH_TYPE_EXACT_IGNORE_CASE;

//
// Common, shared functions.
//
type EncodeMatchRuleFn<T, U> = (
    simpleFieldFilter: SimpleFieldFilter<T>
) => MatchRule<U>;
function createEncodeMatchRuleFn<T, U>(
    exactMatchType: ExactMatchType,
    encode: (val: T) => U
): EncodeMatchRuleFn<T, U> {
    return (simpleFieldFilter: SimpleFieldFilter<T>): MatchRule<U> => {
        switch (simpleFieldFilter.matchType) {
            case exactMatchType: {
                return {
                    data: encode(simpleFieldFilter.value),
                    type: "Exact",
                };
            }
            case MATCH_TYPE_WILDCARD: {
                const value = simpleFieldFilter.value;
                if (isString(value)) {
                    return {
                        data: value,
                        type: "Partial",
                    };
                } else {
                    throw new Error(
                        `Partial filter is expected to contain value of type string, got: ${simpleFieldFilter.value}`
                    );
                }
            }
            default: {
                throw new Error(
                    `encodeMatchRule(), unknown matchType: ${simpleFieldFilter.matchType}`
                );
            }
        }
    };
}

type DecodeMatchRuleFn<T, U> = (
    matchRule: MatchRule<U>
) => SimpleFieldFilter<T>;
function createDecodeMatchRuleFn<T, U>(
    exactMatchType: ExactMatchType,
    decode: (val: U) => T
): DecodeMatchRuleFn<T, U> {
    return (matchRule: MatchRule<U>): SimpleFieldFilter<T> => {
        switch (matchRule.type) {
            case "Exact": {
                return {
                    value: decode(matchRule.data),
                    matchType: exactMatchType,
                };
            }
            case "Partial": {
                return {
                    // NOTE: in the world of SimpleFieldFilter in case of MATCH_TYPE_WILDCARD, value is always a string
                    value: matchRule.data as unknown as T,
                    matchType: MATCH_TYPE_WILDCARD,
                };
            }
            default: {
                return assertNever(matchRule);
            }
        }
    };
}

function encodeOrFilterBase<T, U>(
    encode: EncodeMatchRuleFn<T, U>,
    extractorId: string,
    filters: Array<SimpleOrFilter>
): OrFilter<U> | undefined {
    const relevantFilter = filters.find(
        (f) => f.extractorId === extractorId
    ) as SimpleOrFilter<T> | undefined;
    if (!relevantFilter) {
        return undefined;
    }

    const includeFilters = relevantFilter.filters ? relevantFilter.filters : [];
    const excludeFilters = relevantFilter.exclusionFilters
        ? relevantFilter.exclusionFilters
        : [];

    return {
        include: includeFilters.map(encode),
        exclude: excludeFilters.map(encode),
    };
}

function decodeOrFilterBase<T, U>(
    decode: DecodeMatchRuleFn<T, U>,
    extractorId: string,
    orFilter: OrFilter<U>
): SimpleOrFilter<T> {
    return {
        extractorId,
        filters: orFilter.include.map(decode),
        exclusionFilters: orFilter.exclude.map(decode),
    };
}

//
// Use encodeOrFilter() and decodeOrFilter() when the type in memory is the same as serialized JSON.
// (most of the cases)
//
const encodeMatchRule: <T>(
    simpleFieldFilter: SimpleFieldFilter<T>
) => MatchRule<T> = createEncodeMatchRuleFn(
    MATCH_TYPE_EXACT_IGNORE_CASE,
    (v) => v
);

function encodeOrFilter<T>(
    extractorId: string,
    filters: Array<SimpleOrFilter>
): OrFilter<T> | undefined {
    return encodeOrFilterBase<T, T>(encodeMatchRule, extractorId, filters);
}

const decodeMatchRule: <T>(matchRule: MatchRule<T>) => SimpleFieldFilter<T> =
    createDecodeMatchRuleFn(MATCH_TYPE_EXACT_IGNORE_CASE, (v) => v);

function decodeOrFilter<T>(
    extractorId: string,
    orFilter: OrFilter<T>
): SimpleOrFilter {
    return decodeOrFilterBase(decodeMatchRule, extractorId, orFilter);
}

//
// Date is a special beast, because in JS it's presented as Date object, but it needs to be ISO representation in JSON.
//
const encodeMatchRuleAsDate: (
    simpleFieldFilter: SimpleFieldFilter<Date>
) => MatchRule<string> = createEncodeMatchRuleFn(
    MATCH_TYPE_EXACT_IGNORE_CASE,
    (date: Date) => moment(date).format("YYYY-MM-DD")
);

function encodeOrFilterAsDate(
    extractorId: string,
    filters: Array<SimpleOrFilter>
): OrFilter<string> | undefined {
    return encodeOrFilterBase(encodeMatchRuleAsDate, extractorId, filters);
}

function decodeOrFilterAsDate(
    extractorId: string,
    orFilter: OrFilter<string>
): SimpleOrFilter {
    return decodeOrFilterBase(decodeMatchRuleAsDate, extractorId, orFilter);
}

const decodeMatchRuleAsDate: (
    matchRule: MatchRule<string>
) => SimpleFieldFilter<Date> = createDecodeMatchRuleFn(
    MATCH_TYPE_EXACT_IGNORE_CASE,
    (isoDate) => new Date(isoDate)
);

type FiltersDecoders<T extends object> = {
    [K in NonNullable<keyof T>]: (filters: T) => void;
};

type FiltersEncoded<T extends object> = {
    [K in NonNullable<keyof T>]: T[K];
};

function decodeFilters<T extends { [k: string]: any }>(
    decoders: FiltersDecoders<T>,
    filters: T
) {
    for (const decoder of Object.values(decoders)) {
        decoder(filters);
    }
}

export type { FiltersDecoders, FiltersEncoded };

export {
    decodeFilters,
    encodeOrFilter,
    decodeOrFilter,
    encodeOrFilterAsDate,
    decodeOrFilterAsDate,
    encodeMatchRule,
    createEncodeMatchRuleFn,
    createDecodeMatchRuleFn,
    encodeOrFilterBase,
    decodeOrFilterBase,
};
